import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Loader2, Search, Building2, MapPin, FileText, TrendingUp, X,
  Mail, Phone, Globe, Shield, DollarSign, Calendar, CheckCircle,
  Clock, AlertCircle, ExternalLink, Download
} from "lucide-react";
import { LuxuryBackground, Card, PageHero, inputClassLight as inputClass } from "../components/shared/UIComponents";
import { fmtUSD } from "../utils/currency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const loanLabels = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up", multifamily: "Multifamily", commercial: "Commercial" };
const companyLabels = { bank: "Bank", credit_union: "Credit Union", private_fund: "Private Fund", hedge_fund: "Hedge Fund", family_office: "Family Office", hard_money: "Hard Money", debt_fund: "Debt Fund", institutional: "Institutional", other: "Other" };

export default function LenderDiscovery() {
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterLoan, setFilterLoan] = useState("");
  const [selectedLender, setSelectedLender] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("ic_token");
    axios.get(`${API}/lenders/directory`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setLenders(r.data.lenders || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = lenders.filter(l => {
    const q = search.toLowerCase();
    const matchSearch = !q || (l.full_name || "").toLowerCase().includes(q)
      || (l.company || "").toLowerCase().includes(q)
      || (l.location || "").toLowerCase().includes(q);
    const matchType = !filterType || l.company_type === filterType;
    const matchLoan = !filterLoan || (l.lending_criteria?.loan_types || []).includes(filterLoan);
    return matchSearch && matchType && matchLoan;
  });

  const hasFilters = search || filterType || filterLoan;

  return (
    <div className="relative" data-testid="lender-discovery-page">
      <PageHero eyebrow="Discovery" title="Lending Partners"
        description="Browse verified lenders on the Imaani Capital marketplace. Find the right capital partner for your deal." />
      <div className="relative">
        <LuxuryBackground />
        <section className="relative z-10 mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
          {/* Filters */}
          <Card hover={false} className="mb-8">
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-1">
                <label className="mb-1.5 block text-xs text-emerald-700 font-medium">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#aaa]" />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Name, company, or location..."
                    className={inputClass + " !pl-10"} data-testid="discovery-search" />
                </div>
              </div>
              <div className="w-full md:w-48">
                <label className="mb-1.5 block text-xs text-emerald-700 font-medium">Company Type</label>
                <select value={filterType} onChange={e => setFilterType(e.target.value)} className={inputClass} data-testid="discovery-filter-type">
                  <option value="">All Types</option>
                  {Object.entries(companyLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="w-full md:w-48">
                <label className="mb-1.5 block text-xs text-emerald-700 font-medium">Loan Program</label>
                <select value={filterLoan} onChange={e => setFilterLoan(e.target.value)} className={inputClass} data-testid="discovery-filter-loan">
                  <option value="">All Programs</option>
                  {Object.entries(loanLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              {hasFilters && (
                <button onClick={() => { setSearch(""); setFilterType(""); setFilterLoan(""); }}
                  className="px-3 py-3 rounded-xl text-[#888] hover:text-[#0A0A0A] border border-cream-warm transition-colors" data-testid="discovery-clear-filters">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </Card>

          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-emerald-700" /></div>
          ) : filtered.length === 0 ? (
            <Card hover={false}>
              <div className="text-center py-12 text-[#999]">
                <Building2 className="h-12 w-12 mx-auto mb-3 text-cream/20" />
                <p>No lenders match your criteria.</p>
              </div>
            </Card>
          ) : (
            <>
              <p className="text-sm text-[#888] mb-4">{filtered.length} verified lending partner{filtered.length !== 1 ? "s" : ""}</p>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filtered.map((l, i) => (
                  <motion.div key={l.user_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                    <LenderCard lender={l} onClick={() => setSelectedLender(l.user_id)} />
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </section>
      </div>

      <AnimatePresence>
        {selectedLender && (
          <LenderDetailModal userId={selectedLender} onClose={() => setSelectedLender(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}

function LenderCard({ lender, onClick }) {
  const { full_name, company, company_type, location, bio, lending_criteria, aum, activity } = lender;
  const criteria = lending_criteria || {};
  const initials = (full_name || "L").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);

  return (
    <div onClick={onClick} className="cursor-pointer" data-testid={`lender-card-${lender.user_id}`}>
      <Card hover={true}>
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-400/20 to-gold/10 border border-cream-warm flex items-center justify-center shrink-0">
            <span className="text-xs font-semibold text-gold-dark">{initials}</span>
          </div>
          <div className="min-w-0">
            <div className="font-medium text-[#0A0A0A] text-sm truncate">{full_name}</div>
            {company && <div className="text-xs text-[#888] truncate">{company}</div>}
          </div>
        </div>

        <div className="space-y-2 mb-3">
          {location && <div className="text-xs text-[#888] flex items-center gap-1.5"><MapPin className="h-3 w-3 text-gold/50" />{location}</div>}
          {company_type && <div className="text-xs text-[#888] capitalize">{companyLabels[company_type] || company_type}</div>}
          {aum && <div className="text-xs text-[#888]">AUM: {aum}</div>}
        </div>

        {bio && <p className="text-xs text-[#999] mb-3 line-clamp-2">{bio}</p>}

        {criteria.loan_types?.length > 0 && (
          <div className="flex gap-1.5 flex-wrap mb-3">
            {criteria.loan_types.map(lt => (
              <span key={lt} className="px-2 py-0.5 rounded-md text-[10px] bg-gold/10 text-emerald-700/80 border border-cream-warm">{loanLabels[lt] || lt}</span>
            ))}
          </div>
        )}

        {criteria.states && (
          <div className="text-[10px] text-[#999] mb-3">States: {criteria.states}</div>
        )}

        {(criteria.min_loan || criteria.max_loan) && (
          <div className="text-[10px] text-[#999] mb-3">
            Loan range: {criteria.min_loan ? fmtUSD(criteria.min_loan) : "—"} – {criteria.max_loan ? fmtUSD(criteria.max_loan) : "—"}
          </div>
        )}

        <div className="flex items-center gap-4 pt-3 border-t border-cream-warm">
          <div className="flex items-center gap-1 text-xs text-[#999]"><FileText className="h-3 w-3" />{activity?.term_sheets || 0} offers</div>
          <div className="flex items-center gap-1 text-xs text-[#999]"><TrendingUp className="h-3 w-3" />{activity?.deals_reviewed || 0} deals reviewed</div>
        </div>
      </Card>
    </div>
  );
}

// ── Lender Detail Modal ─────────────────────────────────────
function LenderDetailModal({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const token = localStorage.getItem("ic_token");

  useEffect(() => {
    axios.get(`${API}/lenders/directory/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [userId, token]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col mx-4 sm:mx-0"
        onClick={e => e.stopPropagation()} data-testid="lender-detail-modal">

        {loading || !data ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-700" />
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="px-4 sm:px-6 py-5 border-b border-cream-warm bg-gradient-to-r from-emerald-50 to-white flex-shrink-0">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400/20 to-gold/20 border border-cream-warm flex items-center justify-center">
                    <span className="text-lg font-bold text-gold-dark">
                      {(data.lender.full_name || "L").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#0A0A0A]">{data.lender.full_name}</h2>
                    {data.lender.company && <p className="text-sm text-[#666]">{data.lender.company}</p>}
                    <div className="flex items-center gap-3 mt-1">
                      {data.lender.company_type && (
                        <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold capitalize">
                          {companyLabels[data.lender.company_type] || data.lender.company_type}
                        </span>
                      )}
                      {data.lender.onboarding_complete && (
                        <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
                          <CheckCircle className="h-3 w-3" /> Verified
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button onClick={onClose} className="p-2 rounded-xl hover:bg-cream transition-colors" data-testid="lender-detail-close">
                  <X className="h-5 w-5 text-[#888]" />
                </button>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                {[
                  { label: "Term Sheets", value: data.stats.total_term_sheets, icon: FileText, color: "text-blue-600" },
                  { label: "Deals Funded", value: data.stats.funded_deals, icon: CheckCircle, color: "text-emerald-600" },
                  { label: "Total Offered", value: fmtUSD(data.stats.total_offered), icon: DollarSign, color: "text-gold-dark" },
                  { label: "Deals Reviewed", value: data.stats.deals_reviewed, icon: TrendingUp, color: "text-purple-600" },
                ].map(s => (
                  <div key={s.label} className="text-center p-2 rounded-xl bg-white border border-cream-warm">
                    <s.icon className={`h-4 w-4 mx-auto ${s.color}`} />
                    <div className="text-lg font-bold text-[#0A0A0A] mt-1">{s.value}</div>
                    <div className="text-[10px] text-[#888]">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 px-4 sm:px-6 py-3 border-b border-cream-warm bg-cream/50 flex-shrink-0 overflow-x-auto">
              {["overview", "deals", "documents"].map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)} data-testid={`lender-detail-tab-${tab}`}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab ? "bg-emerald-700 text-white" : "text-[#666] hover:bg-white hover:text-[#0A0A0A]"
                  }`}>
                  {tab === "deals" ? "Deals & Offers" : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5">
              {activeTab === "overview" && <OverviewTab lender={data.lender} />}
              {activeTab === "deals" && <DealsTab termSheets={data.term_sheets} interests={data.interests} />}
              {activeTab === "documents" && <DocumentsTab documents={data.documents} />}
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

// ── Overview Tab ─────────────────────────────────────────────
function OverviewTab({ lender }) {
  const criteria = lender.lending_criteria || {};

  return (
    <div className="space-y-6" data-testid="lender-detail-overview">
      {/* Contact */}
      <div>
        <h3 className="text-sm font-bold text-[#0A0A0A] mb-3">Contact Information</h3>
        <div className="grid grid-cols-2 gap-3">
          {lender.email && (
            <div className="flex items-center gap-2.5 p-3 rounded-xl bg-cream border border-cream-warm">
              <Mail className="h-4 w-4 text-emerald-600" />
              <div>
                <div className="text-[10px] text-[#888]">Email</div>
                <a href={`mailto:${lender.email}`} className="text-sm text-emerald-700 hover:underline">{lender.email}</a>
              </div>
            </div>
          )}
          {lender.phone && (
            <div className="flex items-center gap-2.5 p-3 rounded-xl bg-cream border border-cream-warm">
              <Phone className="h-4 w-4 text-emerald-600" />
              <div>
                <div className="text-[10px] text-[#888]">Phone</div>
                <span className="text-sm text-[#333]">{lender.phone}</span>
              </div>
            </div>
          )}
          {lender.location && (
            <div className="flex items-center gap-2.5 p-3 rounded-xl bg-cream border border-cream-warm">
              <MapPin className="h-4 w-4 text-emerald-600" />
              <div>
                <div className="text-[10px] text-[#888]">Location</div>
                <span className="text-sm text-[#333]">{lender.location}</span>
              </div>
            </div>
          )}
          {lender.website && (
            <div className="flex items-center gap-2.5 p-3 rounded-xl bg-cream border border-cream-warm">
              <Globe className="h-4 w-4 text-emerald-600" />
              <div>
                <div className="text-[10px] text-[#888]">Website</div>
                <a href={lender.website.startsWith("http") ? lender.website : `https://${lender.website}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-sm text-emerald-700 hover:underline flex items-center gap-1">
                  {lender.website} <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bio */}
      {lender.bio && (
        <div>
          <h3 className="text-sm font-bold text-[#0A0A0A] mb-2">About</h3>
          <p className="text-sm text-[#666] leading-relaxed">{lender.bio}</p>
        </div>
      )}

      {/* Lending Criteria */}
      <div>
        <h3 className="text-sm font-bold text-[#0A0A0A] mb-3">Lending Criteria</h3>
        <div className="rounded-xl border border-cream-warm overflow-hidden">
          {[
            { label: "Loan Types", value: criteria.loan_types?.map(t => loanLabels[t] || t).join(", ") },
            { label: "States", value: criteria.states },
            { label: "Min Loan", value: criteria.min_loan ? fmtUSD(criteria.min_loan) : null },
            { label: "Max Loan", value: criteria.max_loan ? fmtUSD(criteria.max_loan) : null },
            { label: "Min FICO", value: criteria.min_fico },
            { label: "Max LTV", value: criteria.max_ltv ? `${criteria.max_ltv}%` : null },
          ].filter(r => r.value).map((row, i) => (
            <div key={row.label} className={`flex items-center justify-between px-4 py-3 ${i % 2 === 0 ? "bg-cream/50" : "bg-white"}`}>
              <span className="text-sm text-[#666]">{row.label}</span>
              <span className="text-sm font-semibold text-[#0A0A0A]">{row.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* AUM */}
      {lender.aum && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-gold/10 border border-gold/20">
          <DollarSign className="h-4 w-4 text-gold-dark" />
          <span className="text-sm font-medium text-gold-dark">AUM: {lender.aum}</span>
        </div>
      )}

      <div className="text-xs text-[#aaa]">Member since {lender.created_at ? new Date(lender.created_at).toLocaleDateString() : "N/A"}</div>
    </div>
  );
}

// ── Deals & Offers Tab ──────────────────────────────────────
function DealsTab({ termSheets, interests }) {
  const tsStatusColors = {
    submitted: "bg-blue-100 text-blue-700",
    accepted: "bg-emerald-100 text-emerald-700",
    withdrawn: "bg-red-100 text-red-600",
    rejected: "bg-gray-100 text-gray-600",
  };

  return (
    <div className="space-y-6" data-testid="lender-detail-deals">
      {/* Term Sheets */}
      <div>
        <h3 className="text-sm font-bold text-[#0A0A0A] mb-3 flex items-center gap-2">
          <FileText className="h-4 w-4 text-emerald-600" />
          Term Sheets ({termSheets.length})
        </h3>
        {termSheets.length === 0 ? (
          <div className="text-center py-8 text-sm text-[#888] bg-cream rounded-xl border border-cream-warm">
            No term sheets submitted yet
          </div>
        ) : (
          <div className="space-y-3">
            {termSheets.map(ts => (
              <div key={ts.ts_id} className="rounded-xl border border-cream-warm bg-white p-4 hover:shadow-sm transition-shadow" data-testid={`ts-card-${ts.ts_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-sm font-semibold text-[#0A0A0A]">
                      {ts.deal_info?.property_address || "Deal"}
                    </div>
                    <div className="text-xs text-[#888]">
                      {loanLabels[ts.deal_info?.loan_type] || ts.deal_info?.loan_type || "—"} &bull; {ts.deal_info?.state || "—"}
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${tsStatusColors[ts.status] || "bg-gray-100 text-gray-600"}`}>
                    {ts.status?.toUpperCase()}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center">
                  {[
                    { label: "Rate", value: `${ts.interest_rate}%` },
                    { label: "Points", value: ts.points },
                    { label: "Loan Amt", value: fmtUSD(ts.loan_amount) },
                    { label: "Term", value: `${ts.term_months}mo` },
                    { label: "Close", value: `${ts.closing_days}d` },
                  ].map(f => (
                    <div key={f.label}>
                      <div className="text-xs font-bold text-[#0A0A0A]">{f.value}</div>
                      <div className="text-[10px] text-[#888]">{f.label}</div>
                    </div>
                  ))}
                </div>
                {ts.conditions && (
                  <div className="mt-2 pt-2 border-t border-cream-warm text-xs text-[#666]">
                    <span className="font-medium text-[#888]">Conditions:</span> {ts.conditions}
                  </div>
                )}
                <div className="mt-2 text-[10px] text-[#aaa]">{new Date(ts.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Interests Expressed */}
      <div>
        <h3 className="text-sm font-bold text-[#0A0A0A] mb-3 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-purple-600" />
          Deals Reviewed ({interests.length})
        </h3>
        {interests.length === 0 ? (
          <div className="text-center py-8 text-sm text-[#888] bg-cream rounded-xl border border-cream-warm">
            No deal interests recorded
          </div>
        ) : (
          <div className="space-y-2">
            {interests.map(i => (
              <div key={i.interest_id} className="flex items-center justify-between p-3 rounded-xl bg-cream border border-cream-warm" data-testid={`interest-${i.interest_id}`}>
                <div>
                  <div className="text-sm font-medium text-[#0A0A0A]">{i.deal_info?.property_address || "Deal"}</div>
                  <div className="text-xs text-[#888]">
                    {loanLabels[i.deal_info?.loan_type] || "—"} &bull; {i.deal_info?.state || "—"}
                    {i.deal_info?.purchase_price && <> &bull; ${parseInt(i.deal_info.purchase_price).toLocaleString()}</>}
                  </div>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    i.deal_info?.stage === "funded" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"
                  }`}>
                    {(i.deal_info?.stage || "").replace(/_/g, " ").toUpperCase() || "N/A"}
                  </span>
                  <div className="text-[10px] text-[#aaa] mt-1">{new Date(i.created_at).toLocaleDateString()}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Documents Tab ───────────────────────────────────────────
function DocumentsTab({ documents }) {
  const docTypeLabels = {
    entity_docs: "Entity Documents",
    purchase_contract: "Purchase Contract",
    id_verification: "ID Verification",
    insurance: "Insurance",
    tax_returns: "Tax Returns",
    compliance: "Compliance",
    agreements: "Agreements",
    license: "License",
    w9: "W-9 Form",
    other: "Other",
  };

  const docsByType = {};
  (documents || []).forEach(doc => {
    const type = doc.doc_type || "other";
    if (!docsByType[type]) docsByType[type] = [];
    docsByType[type].push(doc);
  });

  return (
    <div className="space-y-5" data-testid="lender-detail-documents">
      {documents.length === 0 ? (
        <div className="text-center py-12">
          <Shield className="h-12 w-12 mx-auto mb-3 text-[#ddd]" />
          <p className="text-sm text-[#888]">No compliance documents on file</p>
          <p className="text-xs text-[#aaa] mt-1">Documents such as licenses, agreements, and W-9 forms will appear here once uploaded.</p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200">
            <Shield className="h-4 w-4 text-emerald-600" />
            <span className="text-sm text-emerald-700 font-medium">{documents.length} document{documents.length !== 1 ? "s" : ""} on file</span>
          </div>

          {Object.entries(docsByType).map(([type, docs]) => (
            <div key={type}>
              <h4 className="text-xs font-bold text-[#444] uppercase tracking-wider mb-2">{docTypeLabels[type] || type}</h4>
              <div className="space-y-2">
                {docs.map(doc => (
                  <div key={doc.doc_id} className="flex items-center justify-between p-3 rounded-xl bg-cream border border-cream-warm" data-testid={`doc-${doc.doc_id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="h-4 w-4 text-emerald-600 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-[#0A0A0A] truncate">{doc.original_filename}</div>
                        <div className="text-[10px] text-[#888]">
                          {doc.content_type} &bull; {doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : ""}
                          &bull; {new Date(doc.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    {doc.storage_path && (
                      <button className="p-2 rounded-lg hover:bg-white text-[#888] hover:text-emerald-600 transition-colors" title="Download">
                        <Download className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
